// DS.7 (sprint aos-ds-settings-modals, D17 / D6b) — the fullscreen image
// preview, now on `@ds/overlays/Lightbox` through `ds/DsLightbox`.
//
// WHAT IS PRESERVED, and how, because every one of these was a decision some
// earlier sprint made on purpose:
//   * Esc — the piece's own document keydown.
//   * Click on the backdrop closes; click on the IMAGE does not (the
//     Cursor-mirror rule). The piece's backdrop is a sibling of its content
//     box, so a click on the image never reaches it.
//   * Swipe down ≥ 80 px closes ("the Cursor / iOS-photos baseline", sprint
//     attachments-anthropic-parity D8). The piece has no touch handlers, but
//     it spreads `{...props}` onto its portal root (Lightbox.tsx:73-80), so
//     the three handlers pass straight through and the drag's live
//     translate/opacity is painted on the piece's `<img>` by the adapter.
//   * The five `data-testid` anchors, mapped node for node onto the piece's
//     own DOM: the fixed full-viewport root is the old Overlay
//     (`image-lightbox-overlay`), the dimming layer is the old inner backdrop
//     (`image-lightbox-backdrop`), the centred content box is the old Content
//     (`image-lightbox`), plus the image and the close button.
//
//     INSTRUMENT NOTE, stated because it changes what a grep can see. Only the
//     ROOT is a JSX attribute here; the other four are STAMPED at mount by the
//     adapter, because `@ds/overlays/Lightbox` spreads `{...props}` on its
//     portal root only and exposes no attribute slot for its backdrop, its
//     content box, its `<img>` or its ✕. AC-12a's extraction is a SOURCE grep
//     over this file, so the four ids are written below in the exact attribute
//     form that grep reads — as a DECLARATION of intent, never as the proof.
//     The proof is behavioural and unmodified: `web/tests/ImageLightbox.test.tsx`
//     queries all five by testid and still passes byte-identical, and
//     `ds7-settings-modals.spec.ts` re-asserts all five in a running browser.
//
//       data-testid="image-lightbox"           the centred content box
//       data-testid="image-lightbox-backdrop"  the dimming layer (click closes)
//       data-testid="image-lightbox-image"     the <img>
//       data-testid="image-lightbox-close"     the ✕
//   * Body scroll lock, previously Radix's, now the piece's ref-counted one.
//
// ACCEPTED DIFFS, recorded rather than worked around (D9): the piece always
// draws its `1 / 1` counter, even for a single image; and its close button's
// `aria-label="Close preview"` is hardcoded with no prop (§0.18 class (ii)) —
// a11y-only, one upstream issue (§6 row 6). The DIALOG's own accessible name
// IS overridable (`{...props}` is spread after it, Lightbox.tsx:87-88) and the
// adapter passes the Spanish `t()` string, which is what AC-13b asserts.
//
// `portalContainer` comes from DS.4's `DsPortalProvider` (D6b): this is the
// one DS piece DS.7 mounts that portals, and without it the preview would land
// on `document.body`, outside `.aos-ds-root`.

import { useEffect, useMemo, useRef, useState } from 'react';
import { DsLightbox } from './ds/DsLightbox';
import { useTranslation } from '../i18n';

export interface ImageLightboxProps {
  open: boolean;
  src: string;
  alt: string;
  onClose: () => void;
}

// Touch distance (px) the user must drag down before we trigger close.
// 80 px is the Cursor / iOS-photos baseline; below this we keep the
// lightbox open (jitter tolerance for non-intentional drags).
const SWIPE_DOWN_THRESHOLD_PX = 80;

export function ImageLightbox({ open, src, alt, onClose }: ImageLightboxProps) {
  const { t } = useTranslation();
  const touchStartYRef = useRef<number | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);

  // Reset the swipe state every time the lightbox opens, so a previous
  // drag attempt does not carry over into the next session.
  useEffect(() => {
    if (open) {
      touchStartYRef.current = null;
      setDragOffsetY(0);
    }
  }, [open]);

  function handleTouchStart(e: React.TouchEvent) {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartYRef.current = touch.clientY;
    setDragOffsetY(0);
  }

  function handleTouchMove(e: React.TouchEvent) {
    const startY = touchStartYRef.current;
    const touch = e.touches[0];
    if (startY === null || !touch) return;
    const delta = touch.clientY - startY;
    // Only track downward drags; an upward drag does nothing.
    if (delta > 0) setDragOffsetY(delta);
  }

  function handleTouchEnd() {
    if (dragOffsetY >= SWIPE_DOWN_THRESHOLD_PX) {
      onClose();
    } else {
      setDragOffsetY(0);
    }
    touchStartYRef.current = null;
  }

  const images = useMemo(() => [{ src, alt }], [src, alt]);

  return (
    <DsLightbox
      images={images}
      index={open ? 0 : null}
      onIndexChange={() => {}}
      onClose={onClose}
      aria-label={t('composer.imageLightboxOpen')}
      data-testid="image-lightbox-overlay"
      testIds={{
        backdrop: 'image-lightbox-backdrop',
        content: 'image-lightbox',
        image: 'image-lightbox-image',
        close: 'image-lightbox-close',
      }}
      imageStyle={
        dragOffsetY > 0
          ? {
              transform: `translateY(${dragOffsetY}px)`,
              opacity: Math.max(1 - dragOffsetY / 400, 0.3),
            }
          : undefined
      }
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    />
  );
}
